import { getData } from 'utils/sesionStorage';
import { redirect } from 'react-router-dom';

import * as actionsGlobal from '@redux/global/actions';
import * as reportingActions from 'pages/reporting/controllers/actions';
import * as folderActions from 'pages/folders/controllers/actions';
import * as actionsGridView from 'common/components/grid-view/controllers/actions';
import { getParamsFromStoreRedux } from 'store/utils';
import { isAuthed } from 'utils/common/session';
import { getReturnUrlFromRequestLoader } from 'utils/common/route';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';
import { queryBrandingNavigation } from 'hooks';
import { defineRulesFor, defineRulesForWorkflow } from 'common/casl/ability';
import { ensureEulaData } from 'pages/eula/useEulaQuery';
import { ensureRivirTCData } from 'pages/eula/check-rivir-tc/useRivirTCQuery';
import { ensureRenewalNotificationData } from 'pages/renewal-notification/useRenewalNotificationQuery';
import { getWalmartAuthorizationParams } from 'hooks/router';
import { isProdEnv } from 'utils/checkDeployEnv';

export const homePageLoader =
  (queryClient, ability) =>
  async ({ request }) => {
    const { dispatch, getState } = getParamsFromStoreRedux();

    const url = new URL(request.url);
    const pathName = url.pathname;
    const returnUrl = getReturnUrlFromRequestLoader(request);
    const isAuthenticated = isAuthed();

    const { walmartSearchQuery } = getWalmartAuthorizationParams(request);
    const isProductionEnv = isProdEnv();

    if (isAuthenticated) {
      const { userInfo } = await ensureUserInfoLoaded({
        queryClient,
        returnUrl,
      });

      if (walmartSearchQuery) {
        throw redirect(`/waiting${walmartSearchQuery}`);
      }

      if (userInfo?.id) {
        const eulaData = await ensureEulaData(queryClient, userInfo.id);
        const rivirTCData = await ensureRivirTCData(queryClient, userInfo.id);
        const renewalNotificationData = await ensureRenewalNotificationData(
          queryClient,
          userInfo.id
        );

        // No active EULA for this user means there is nothing to accept
        if (eulaData && !eulaData.isAccepted) {
          throw redirect(
            `/eula?returnUrl=${encodeURIComponent(returnUrl ?? pathName)}`
          );
        }

        if (!rivirTCData?.isAccepted) {
          throw redirect(
            `/check-rivir-tc?returnUrl=${encodeURIComponent(
              returnUrl ?? pathName
            )}`
          );
        }

        if (!renewalNotificationData?.isAccepted) {
          throw redirect(
            `/renewal-notification?returnUrl=${encodeURIComponent(
              returnUrl ?? pathName
            )}`
          );
        }

        if (!userInfo?.hasCreditCard && !isProductionEnv) {
          throw redirect(
            `/check-credit-card-info?returnUrl=${encodeURIComponent(
              returnUrl ?? pathName
            )}`
          );
        }
      }

      // Only after EULA is confirmed, continue with other logic
      try {
        let brandingData = null;

        if (userInfo?.id) {
          const brandingResponse = await queryBrandingNavigation({
            queryClient,
            userId: userInfo.id,
          });

          brandingData = brandingResponse?.data;
        }

        if (userInfo && brandingData) {
          const workflowRoles = userInfo?.workflowRoles ?? [];

          const permissionRules = defineRulesFor(userInfo, brandingData);
          const workflowRules = defineRulesForWorkflow(
            userInfo,
            workflowRoles,
            brandingData
          );
          const rules = [...permissionRules, ...workflowRules];
          ability.update(rules);
        }
      } catch (error) {
        console.error('Failed to load branding or set permissions:', error);
      }
    }

    // Your existing dispatch logic continues unchanged...
    const searchCategoriesEffected =
      getState()?.searchCategoriesEffected ?? null;
    const user = getData('User');

    let logging = {
      type: 'url',
      date: new Date(),
      email: user ? user.email : '',
      browser: navigator.userAgent,
      pathName: pathName,
    };

    dispatch(actionsGlobal.updateLogging(logging));

    dispatch(actionsGridView.updateDetailCurrentItemsSelection([]));
    dispatch(actionsGridView.updateVisibleSelection(false));
    dispatch(actionsGridView.updateItemCurrentSelection(null));
    dispatch(actionsGridView.updateItemsSelectionDialog(null));
    dispatch(actionsGridView.updateItemsSelection([]));
    dispatch(actionsGridView.updatePagesSelection([]));
    dispatch(actionsGridView.updateItemPageSelection([]));

    dispatch(actionsGridView.updateCountPageSelection([]));
    dispatch(actionsGridView.updateIsDisplayMask(true));
    dispatch(actionsGridView.updateVisible(false));
    dispatch(actionsGridView.updateSizeCurrent([1, 0]));
    dispatch(actionsGridView.updateTypeViewCurrent(null));
    dispatch(actionsGridView.updateSearchCurrent(null));

    searchCategoriesEffected === null &&
      dispatch(actionsGridView.setUserSearchCategoriesEffected([]));

    dispatch(actionsGlobal.searchAllItems(''));
    dispatch(actionsGlobal.updateAddToFolder(false));

    dispatch(actionsGlobal.updateIsBackFromQuery(false));
    dispatch(actionsGlobal.updateIsBackFromReporting(false));
    dispatch(reportingActions.updateModeView(''));
    dispatch(folderActions.resetFolderPaging());

    return {
      pathName,
      user,
      timestamp: new Date(),
    };
  };
